<template>
  <form class="sms-login-form" @submit.prevent="handleSmsLogin">
    <div class="form-group">
      <label for="phone">{{ $t('auth.sms.phoneLabel') }}</label>
      <InputText
        id="phone"
        v-model="form.phone"
        type="tel"
        :placeholder="$t('auth.sms.phonePlaceholder')"
        :class="{ 'p-invalid': errors.phone }"
        required
        maxlength="11"
        @input="validatePhone"
      />
      <small v-if="errors.phone" class="error-message">{{
        errors.phone
      }}</small>
    </div>

    <div class="form-group">
      <label for="code">{{ $t('auth.sms.codeLabel') }}</label>
      <div class="code-input-container">
        <InputText
          id="code"
          v-model="form.code"
          type="text"
          :placeholder="$t('auth.sms.codePlaceholder')"
          :class="{ 'p-invalid': errors.code }"
          required
          maxlength="6"
        />
        <Button
          :label="sendCodeButtonText"
          :disabled="!canSendCode"
          :loading="sendingCode"
          class="send-code-button"
          severity="secondary"
          size="small"
          @click="sendVerificationCode"
        />
      </div>
      <small v-if="errors.code" class="error-message">{{ errors.code }}</small>
    </div>

    <div v-if="authStore.error" class="error-message global-error">
      {{ authStore.error }}
    </div>

    <div class="form-actions">
      <Button
        type="submit"
        :label="$t('auth.sms.loginRegisterButton')"
        icon="pi pi-sign-in"
        :loading="authStore.isLoading"
        class="login-button"
      />
    </div>
  </form>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useSmsAuthService } from '@/services/smsAuthService'
import { useAuthStore } from '@/stores/authStore'

const { t } = useI18n()

interface Props {
  onSuccess?: () => void
}

const props = withDefaults(defineProps<Props>(), {})

const authStore = useAuthStore()
const smsAuthService = useSmsAuthService()

const form = reactive({
  phone: '',
  code: ''
})

const errors = reactive({
  phone: '',
  code: ''
})

const sendingCode = ref(false)
const codeSent = ref(false)
const countdown = ref(0)
let countdownTimer: ReturnType<typeof setInterval> | null = null

const canSendCode = computed(() => {
  return form.phone.length === 11 && !sendingCode.value && countdown.value === 0
})

const sendCodeButtonText = computed(() => {
  if (sendingCode.value) {
    return t('auth.sms.sending')
  }
  if (countdown.value > 0) {
    return t('auth.sms.resendIn', { seconds: countdown.value })
  }
  return t('auth.sms.sendCode')
})

const validatePhone = () => {
  const phonePattern = /^1[3-9]\d{9}$/
  if (form.phone && !phonePattern.test(form.phone)) {
    errors.phone = t('auth.sms.invalidPhone')
  } else {
    errors.phone = ''
  }
}

const startCountdown = () => {
  countdown.value = 60
  countdownTimer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(countdownTimer!)
      countdownTimer = null
    }
  }, 1000)
}

const sendVerificationCode = async () => {
  if (!canSendCode.value) return

  // 验证手机号
  validatePhone()
  if (errors.phone) return

  sendingCode.value = true
  try {
    const result = await smsAuthService.sendCode(form.phone)
    if (result.success) {
      codeSent.value = true
      startCountdown()
    } else {
      errors.phone = result.message || t('auth.sms.sendFailed')
    }
  } catch (error: any) {
    errors.phone = error.message || t('auth.sms.sendFailed')
  } finally {
    sendingCode.value = false
  }
}

const validateForm = () => {
  errors.phone = ''
  errors.code = ''

  if (!form.phone.trim()) {
    errors.phone = t('auth.sms.phoneRequired')
    return false
  }

  if (!form.code.trim()) {
    errors.code = t('auth.sms.codeRequired')
    return false
  }

  if (form.phone.length !== 11) {
    errors.phone = t('auth.sms.invalidPhone')
    return false
  }

  if (form.code.length !== 6) {
    errors.code = t('auth.sms.invalidCode')
    return false
  }

  return true
}

const handleSmsLogin = async () => {
  if (!validateForm()) return

  // 立即重置加载状态，确保按钮可以显示加载动画
  authStore.resetLoadingState()

  const success = await smsAuthService.login({
    phone: form.phone,
    code: form.code
  })

  if (success) {
    console.log('SmsLoginForm: Login successful')
    props.onSuccess?.()
    resetForm()
  } else {
    // 登录失败，重置加载状态
    authStore.resetLoadingState()
  }
}

const resetForm = () => {
  form.phone = ''
  form.code = ''
  errors.phone = ''
  errors.code = ''
  codeSent.value = false
  countdown.value = 0
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

// 暴露重置表单方法给父组件
defineExpose({
  resetForm
})

onMounted(() => {
  // 组件挂载时重置状态
  authStore.resetLoadingState()
})

onUnmounted(() => {
  // 组件卸载时清理定时器
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
})
</script>

<style scoped>
.sms-login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group input {
  width: 100%;
  box-sizing: border-box;
  background: #2a2a2a;
  border: 1px solid #444;
  color: #fff;
}

.form-group input::placeholder {
  color: #888;
}

.form-group input:focus {
  outline: none;
  border-color: var(--p-primary-400);
  box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
}

.form-group label {
  font-weight: 500;
  color: var(--p-primary-400);
  font-size: 14px;
}

.error-message {
  color: #e74c3c;
  font-size: 12px;
  margin-top: 4px;
}

.global-error {
  background: #2d1a1a;
  border: 1px solid #e74c3c;
  border-radius: 6px;
  padding: 12px;
  text-align: center;
}

.form-actions {
  margin-top: 10px;
}

.login-button {
  width: 100%;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
}

.form-footer {
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #333;
}

.form-footer p {
  margin: 0;
  color: #ccc;
  font-size: 14px;
}

.form-footer a {
  color: var(--p-primary-400);
  text-decoration: none;
  font-weight: 500;
}

.form-footer a:hover {
  text-decoration: underline;
}

.code-input-container {
  position: relative;
  width: 100%;
  display: flex;
  gap: 8px;
}

.code-input-container input {
  flex: 1;
  background: #2a2a2a;
  border: 1px solid #444;
  color: #fff;
}

.code-input-container input::placeholder {
  color: #888;
}

.code-input-container input:focus {
  outline: none;
  border-color: var(--p-primary-400);
  box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
}

.send-code-button {
  min-width: 100px;
  white-space: nowrap;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .code-input-container {
    flex-direction: column;
  }

  .send-code-button {
    min-width: auto;
    width: 100%;
  }
}
</style>
